import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Modal,
} from "reactstrap";
import { Triangle } from "react-loader-spinner";
import toast from "react-hot-toast";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import AdvertisersService from "../../../services/OrderingServices/AdvertisersService";
import AdStatsService from "../../../services/OrderingServices/AdStatsService";

const AdvertiserDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [advertiser, setAdvertiser] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [selectedAdDetails, setSelectedAdDetails] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const CDN_BASE_URL = window.APP_CONFIG?.api3?.baseapi || "";

  useEffect(() => {
    fetchAdvertiserDetail();
  }, [id]);

  async function fetchAdvertiserDetail() {
    try {
      setLoading(true);
      const response = await AdvertisersService.getAdvertiserById(id);
      if (response) {
        setAdvertiser(response);
      }
    } catch (error) {
      toast.error("Failed to load advertiser details");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  async function openAdModal(ad) {
    try {
      setIsPreviewOpen(true);
      setPreviewLoading(true);
      const response = await AdStatsService.getAdSummary(ad.id);
      setSelectedAdDetails(response);
    } catch (error) {
      toast.error("Failed to load ad details");
      console.error(error);
      setIsPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  }

  const getMediaUrl = (ad) => {
    if (ad.mediaType === "Video" && ad.thumbnailFile) {
      return `${CDN_BASE_URL}${ad.thumbnailFile}`;
    }
    return `${CDN_BASE_URL}${ad.mediaPath}`;
  };

  const MediaCard = ({ ad }) => {
    console.log("🚀 ~ MediaCard ~ ad:", ad);
    return (
      <Card
        className="media-card h-100 border-0"
        style={{ cursor: "pointer" }}
        onClick={() => navigate(`/ads/${ad.id}`)}
      >
        <div className="media-preview position-relative">
          <img
            src={getMediaUrl(ad)}
            alt={ad.title}
            className="w-100"
            style={{
              height: "180px",
              objectFit: "cover",
              backgroundColor: "#f8f9fa",
            }}
            onError={(e) => {
              e.target.src =
                "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect width='100' height='100' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23999' font-family='sans-serif' font-size='14'%3ENo Image%3C/text%3E%3C/svg%3E";
            }}
          />
          <div className="position-absolute top-0 end-0 p-2">
            <Badge className="tag-badge" style={{ fontSize: "10px" }}>
              {ad.mediaType}
            </Badge>
          </div>

          <div className="position-absolute top-0 start-0 p-2">
            <Badge className="tag-badge" style={{ fontSize: "10px" }}>
              {ad.adFormat}
            </Badge>
          </div>
        </div>
        <CardBody className="px-3 py-2 d-flex justify-content-between align-items-center">
          <h6
            className="mb-0 text-truncate"
            style={{ fontSize: "14px", fontWeight: 600 }}
          >
            {ad.title}{" "}
            {ad.durationSeconds && <span>({ad.durationSeconds}s)</span>}
          </h6>

          {/* Settings Icon Button */}
          <Button
            color="light"
            size="sm"
            onClick={(e) => {
              e.stopPropagation(); // prevent parent card click
              openAdModal(ad);
            }}
            style={{
              padding: "4px 6px",
              borderRadius: "50%",
              minWidth: "auto",
            }}
          >
            <i className="bx bx-cog" size={12} />
          </Button>
        </CardBody>
      </Card>
    );
  };

  if (loading) {
    return (
      <div
        style={{ display: "flex", justifyContent: "center", marginTop: "80px" }}
      >
        <Triangle color="#435ebe" height={80} width={80} />
      </div>
    );
  }

  if (!advertiser) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Advertiser Not Found" pageTitle="Advertisers" />
          <Card>
            <CardBody className="text-center py-5">
              <h5 className="text-muted">Advertiser details not found</h5>
              <Button
                color="primary"
                onClick={() => navigate("/advertiser-list")}
                className="mt-3"
              >
                Back to List
              </Button>
            </CardBody>
          </Card>
        </Container>
      </div>
    );
  }

  return (
    <React.Fragment>
      <style>{`
        .media-card {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          border-radius: 10px;
          overflow: hidden;
        }

        .media-preview {
          overflow: hidden;
          background: #f8f9fa;
          position: relative;
        }

        .media-preview img {
          transition: transform 0.3s ease;
        }

        .media-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
        }

        .media-card:hover .media-preview img {
          transform: scale(1.05);
        }

        .tag-badge {
          font-size: 11px;
          font-weight: 600;
          padding: 6px 14px;
          border-radius: 20px;
          background: linear-gradient(
            135deg,
            rgba(255, 140, 0, 0.9),
            rgba(255, 165, 0, 0.85)
          );
          color: #fff;
          backdrop-filter: blur(6px);
          box-shadow: 0 3px 8px rgba(255, 140, 0, 0.35);
          letter-spacing: 0.3px;
        }
      `}</style>

      <div className="page-content">
        <div className="container-fluid">
          <BreadCrumb
            title={advertiser?.advertiserName}
            pageTitle="Advertisers"
          />
        </div>

        <Container fluid>
          <Row>
            <Col lg="12">
              <Card className="border-0 shadow-sm mb-3">
                <CardHeader className="bg-light border-bottom">
                  <h5 className="mb-0">Advertiser Information</h5>
                </CardHeader>

                <CardBody className="py-3">
                  <Row className="g-2">
                    <Col md="2">
                      <small className="text-muted fw-semibold">
                        Advertiser Name
                      </small>
                      <div className="small">{advertiser?.advertiserName}</div>
                    </Col>

                    <Col md="2">
                      <small className="text-muted fw-semibold">Email</small>
                      <div className="small">{advertiser?.advertiserEmail}</div>
                    </Col>

                    <Col md="2">
                      <small className="text-muted fw-semibold">
                        Contact Number
                      </small>
                      <div className="small">
                        {advertiser?.advertiserContact || "—"}
                      </div>
                    </Col>

                    <Col md="2">
                      <small className="text-muted fw-semibold">Location</small>
                      <div className="small">
                        {advertiser?.advertiserLocation || "—"}
                      </div>
                    </Col>

                    <Col md="2">
                      <small className="text-muted fw-semibold">
                        Contact Person
                      </small>
                      <div className="small">
                        {advertiser?.contactPerson || "—"}
                      </div>
                    </Col>

                    <Col md="2">
                      <small className="text-muted fw-semibold">
                        Contact Phone
                      </small>
                      <div className="small">
                        {advertiser?.contactPhone || "—"}
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Ads Card Grid */}
          <Row>
            <Col lg="12">
              <Card className="border-0 shadow-sm">
                <CardHeader className="bg-light border-bottom">
                  <h5 className="mb-0">
                    Associated Media ({advertiser?.ads?.length || 0})
                  </h5>
                </CardHeader>
                <CardBody>
                  {advertiser?.ads && advertiser.ads.length > 0 ? (
                    <Row className="g-3">
                      {advertiser.ads.map((ad) => (
                        <Col
                          key={ad.id}
                          xs="12"
                          md="6"
                          lg="2"
                          // style={{ flex: "0 0 20%", maxWidth: "20%" }}
                        >
                          <MediaCard ad={ad} />
                        </Col>
                      ))}
                    </Row>
                  ) : (
                    <div className="text-center py-5">
                      <i className="ri-image-2-line display-4 text-muted mb-3" />
                      <p className="text-muted">
                        No ads associated with this advertiser
                      </p>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        {/* Ad Summary Modal */}
        <Modal
          isOpen={isPreviewOpen}
          toggle={() => setIsPreviewOpen(false)}
          size="xl"
          centered
          contentClassName="border-0 shadow-lg"
        >
          <div className="modal-header border-0 pb-0">
            <button
              type="button"
              className="btn-close"
              onClick={() => setIsPreviewOpen(false)}
              aria-label="Close"
            />
          </div>

          <div className="modal-body p-0">
            {previewLoading ? (
              <div
                className="d-flex align-items-center justify-content-center"
                style={{ minHeight: "400px" }}
              >
                <Triangle color="#435ebe" height={60} width={60} />
              </div>
            ) : selectedAdDetails ? (
              <Row className="g-0">
                {/* LEFT SIDE PREVIEW */}
                <Col md="7" className="bg-light">
                  <div
                    className="p-4 d-flex align-items-center justify-content-center"
                    style={{ minHeight: "500px" }}
                  >
                    {selectedAdDetails.adType === "Video" ? (
                      <video
                        src={`${CDN_BASE_URL}${selectedAdDetails.mediaLink}`}
                        controls
                        autoPlay
                        className="w-100 shadow-sm"
                        style={{
                          borderRadius: "4px",
                          maxHeight: "500px",
                        }}
                      />
                    ) : (
                      <img
                        src={`${CDN_BASE_URL}${selectedAdDetails.mediaLink}`}
                        alt={selectedAdDetails.adTitle}
                        className="w-100 shadow-sm"
                        style={{
                          borderRadius: "4px",
                          objectFit: "contain",
                          maxHeight: "500px",
                        }}
                      />
                    )}
                  </div>
                </Col>

                {/* RIGHT SIDE DETAILS */}
                <Col md="5">
                  <div className="p-4">
                    <h3
                      className="mb-1 fw-semibold"
                      style={{ fontSize: "1.5rem", color: "#1a1a1a" }}
                    >
                      {selectedAdDetails.adTitle}
                    </h3>

                    <div className="border-top pt-4">
                      <div className="row g-3">
                        <div className="col-6">
                          <div className="mb-3">
                            <div
                              className="text-uppercase text-muted mb-1"
                              style={{
                                fontSize: "0.75rem",
                                fontWeight: "600",
                                letterSpacing: "0.5px",
                              }}
                            >
                              Media Type
                            </div>
                            <div
                              style={{ fontSize: "0.95rem", color: "#2d3748" }}
                            >
                              {selectedAdDetails.adType}
                            </div>
                          </div>
                        </div>

                        <div className="col-6">
                          <div className="mb-3">
                            <div
                              className="text-uppercase text-muted mb-1"
                              style={{
                                fontSize: "0.75rem",
                                fontWeight: "600",
                                letterSpacing: "0.5px",
                              }}
                            >
                              Total Impressions
                            </div>
                            <div
                              style={{ fontSize: "0.95rem", color: "#2d3748" }}
                            >
                              {selectedAdDetails.totalImpressions}
                            </div>
                          </div>
                        </div>

                        <div className="col-6">
                          <div className="mb-3">
                            <div
                              className="text-uppercase text-muted mb-1"
                              style={{
                                fontSize: "0.75rem",
                                fontWeight: "600",
                                letterSpacing: "0.5px",
                              }}
                            >
                              Total Visible Seconds
                            </div>
                            <div
                              style={{ fontSize: "0.95rem", color: "#2d3748" }}
                            >
                              {selectedAdDetails.totalVisibleSeconds}s
                            </div>
                          </div>
                        </div>

                        <div className="col-6">
                          <div className="mb-3">
                            <div
                              className="text-uppercase text-muted mb-1"
                              style={{
                                fontSize: "0.75rem",
                                fontWeight: "600",
                                letterSpacing: "0.5px",
                              }}
                            >
                              Total Played Seconds
                            </div>
                            <div
                              style={{ fontSize: "0.95rem", color: "#2d3748" }}
                            >
                              {selectedAdDetails.totalPlayedSeconds}s
                            </div>
                          </div>
                        </div>

                        <div className="col-6">
                          <div className="mb-3">
                            <div
                              className="text-uppercase text-muted mb-1"
                              style={{
                                fontSize: "0.75rem",
                                fontWeight: "600",
                                letterSpacing: "0.5px",
                              }}
                            >
                              Completed Plays
                            </div>
                            <div
                              style={{ fontSize: "0.95rem", color: "#2d3748" }}
                            >
                              {selectedAdDetails.completedPlays}
                            </div>
                          </div>
                        </div>

                        <div className="col-6">
                          <div className="mb-3">
                            <div
                              className="text-uppercase text-muted mb-1"
                              style={{
                                fontSize: "0.75rem",
                                fontWeight: "600",
                                letterSpacing: "0.5px",
                              }}
                            >
                              Unique Devices
                            </div>
                            <div
                              style={{ fontSize: "0.95rem", color: "#2d3748" }}
                            >
                              {selectedAdDetails.uniqueDevices}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            ) : null}
          </div>
        </Modal>
      </div>
    </React.Fragment>
  );
};

export default AdvertiserDetail;
